import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const DailyChecklistForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // General information
    date: '',
    jobNumber: '',
    
    // Morning Start section
    MORNING_START: {
      DRESSED_IN_PPE_AND_COMPANY_UNIFORMS: { status: '', date: '' },
      PARK_VEHICLES_IN_CORRECT_SPOT_NOT_DRIVEWAY: { status: '', date: '' },
      MAKE_CONTACT_WITH_CLIENT: { status: '', date: '' },
      PRE_CONSTRUCTION_GROUND_WALK_TO_REVIEW_SITE_CHANGES: { status: '', date: '' },
      SET_UP_LADDER_CORRECTLY_TIE_OFF_GUTTER_GUARD: { status: '', date: '' },
      PRE_CONSTRUCTION_ROOF_WALK_TO_REVIEW_ROOF_CHANGES: { status: '', date: '' },
      CHECK_ALL_TOOLS_AND_EQUIPMENT: { status: '', date: '' },
      SAFETY_BUCKET_ON_SITE: { status: '', date: '' },
      morningStartNotes: ''
    },
    // Lunch and Afternoon section
    LUNCH_AND_AFTERNOON: {
      HOUSEKEEPING_TRASH_NAILS_PAPER_GUTTERS: { status: '', date: '' },
      NO_LOUD_MUSIC_PLAYING: { status: '', date: '' },
      BREAK_TIME_OFF_SITE: { status: '', date: '' },
      RETURN_TO_WORK: { status: '', date: '' },
      MAKE_CONTACT_WITH_CLIENT: { status: '', date: '' },
      CHECK_ALL_TOOLS_AND_MATERIAL: { status: '', date: '' },
      VERIFY_LADDER_SAFETY: { status: '', date: '' },
      VERIFY_ROOF_SAFETY: { status: '', date: '' },
      COMMUNICATE_ANY_CHANGES_WITH_CLIENT_AND_TEAM: { status: '', date: '' },
      TAKE_PROGRESS_PHOTOS: { status: '', date: '' },
      lunchAndAfternoonNotes: ''
    },
    // End of Day section
    END_OF_DAY: {
      DEMOBILIZE_TOOLS_AND_EQUIPMENT_FROM_ROOF: { status: '', date: '' },
      SECURE_ALL_MATERIAL_ON_ROOF: { status: '', date: '' },
      MAKE_SURE_ROOF_IS_DRIED_IN: { status: '', date: '' },
      COMPLETE_HOUSEKEEPING_WALK_ON_ROOF: { status: '', date: '' },
      COMPLETE_HOUSEKEEPING_WALK_ON_SITE: { status: '', date: '' },
      REMOVE_OR_SECURE_ALL_TRASH_AND_DEBRIS: { status: '', date: '' },
      TAKE_PROGRESS_PHOTO: { status: '', date: '' },
      CHECK_OUT_WITH_CLIENT_AND_TOPSAIL_TEAM: { status: '', date: '' },
      endOfDayNotes: ''
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'dailyChecklist') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          // Get project data
          const projectData = await fetchProjectData(projectId);
          console.log('Project data for daily checklist form:', projectData);
          
          // Handle the case where the project is nested in a 'project' property
          const actualProject = projectData.project || projectData;
          
          // Prefill the form data
          const prefilled = { ...formData };
          
          // Set today's date in YYYY-MM-DD format
          const now = new Date();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const year = now.getFullYear();
          prefilled.date = `${year}-${month}-${day}`;
          
          // Set job number
          if (actualProject.jobNumber) {
            prefilled.jobNumber = actualProject.jobNumber;
          } else if (actualProject.originalLead && actualProject.originalLead.jobNumber) {
            prefilled.jobNumber = actualProject.originalLead.jobNumber;
          }
          
          console.log('Prefilled daily checklist form data:', prefilled);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling daily checklist form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (section, field, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: typeof prev[section][field] === 'object' && subField 
          ? { ...prev[section][field], [subField]: value }
          : value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Daily Checklist updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'dailyChecklist',
          formData
        })).unwrap();
        showToast('Daily Checklist saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Daily Checklist', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderChecklistSection = (title, section, sectionData) => {
    const items = Object.entries(sectionData).filter(([key]) => key !== `${section}Notes`);
    
    return (
      <div className="mb-8">
        <div className="bg-gray-100 px-3 py-2 font-bold border border-gray-300 text-gray-800">
          {title}
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2 w-3/5 text-left">Item</th>
              <th className="border border-gray-300 p-2 w-1/5 text-center">Status</th>
              <th className="border border-gray-300 p-2 w-1/5 text-center">Date</th>
            </tr>
          </thead>
          <tbody>
            {items.map(([key, value]) => (
              <tr key={key}>
                <td className="border border-gray-300 p-2 text-sm">
                  {formatItemLabel(key)}
                </td>
                <td className="border border-gray-300 p-1 text-center">
                  <select
                    value={value.status}
                    onChange={(e) => handleChange(section, key, 'status', e.target.value)}
                    className="w-full p-1 border border-gray-200 rounded"
                  >
                    <option value="">Select</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="na">N/A</option>
                  </select>
                </td>
                <td className="border border-gray-300 p-1 text-center">
                  <input
                    type="date"
                    value={value.date}
                    onChange={(e) => handleChange(section, key, 'date', e.target.value)}
                    className="w-full p-1 border border-gray-200 rounded"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2">
          <div className="font-medium text-sm mb-1">Notes:</div>
          <textarea
            value={sectionData[`${section}Notes`] || ''}
            onChange={(e) => handleChange(section, `${section}Notes`, null, e.target.value)}
            className="w-full border border-gray-300 p-2 h-20 text-sm"
            placeholder="Add notes here..."
          />
        </div>
      </div>
    );
  };

  // Helper function to format keys into readable labels
  const formatItemLabel = (key) => {
    return key.replace(/_/g, ' ');
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <h1 class="form-title">DAILY CHECK LIST</h1>
        <div style="width: 200px;">
          ${formatFormField('Date', formData.date)}
          ${formatFormField('Job Number', formData.jobNumber)}
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">MORNING START</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.MORNING_START)
              .filter(([key]) => key !== 'morningStartNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.MORNING_START.morningStartNotes || ''}</div>
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">LUNCH AND AFTERNOON</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.LUNCH_AND_AFTERNOON)
              .filter(([key]) => key !== 'lunchAndAfternoonNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.LUNCH_AND_AFTERNOON.lunchAndAfternoonNotes || ''}</div>
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">END OF DAY</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.END_OF_DAY)
              .filter(([key]) => key !== 'endOfDayNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.END_OF_DAY.endOfDayNotes || ''}</div>
        </div>
      </div>
      
      <div class="form-footer">
        <p>*Continue to communicate with your client daily</p>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'Daily Check List', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/topsail-logo-small.png" 
            alt="Topsail roofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">Topsail roofing</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>205 E Avenida Magdalena</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 370-8612</p>
          <p className="uppercase">topsailroofing.com</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <div className="flex justify-between items-start mb-6">
        <h1 className="text-2xl font-bold">DAILY CHECK LIST</h1>
        <div className="flex flex-col space-y-2 w-64">
          <div className="flex items-center justify-between">
            <label className="font-medium">Date:</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="w-40 p-1 border border-gray-300"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="font-medium">Job Number:</label>
            <input
              type="text"
              name="jobNumber"
              value={formData.jobNumber}
              onChange={(e) => setFormData({...formData, jobNumber: e.target.value})}
              className="w-40 p-1 border border-gray-300"
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {renderChecklistSection('MORNING START', 'MORNING_START', formData.MORNING_START)}
        {renderChecklistSection('LUNCH AND AFTERNOON', 'LUNCH_AND_AFTERNOON', formData.LUNCH_AND_AFTERNOON)}
        {renderChecklistSection('END OF DAY', 'END_OF_DAY', formData.END_OF_DAY)}

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default DailyChecklistForm;
